<template>
    <Teleport to="body">
        <Transition name="fade">
            <dialog
                ref="dialogEL"
                class="max-w-full max-h-full backdrop:bg-black-900 backdrop:bg-opacity-20 backdrop:backdrop-filter backdrop:backdrop-blur"
                @close="close">
                <div
                    class="fixed top-0 left-0 w-full h-full flex flex-col items-center justify-center z-50 text-left bg-black-900 transition-all duration-300 ease-out"
                    :class="{ 'pointer-events-all bg-opacity-90': active, 'pointer-events-none bg-opacity-0': !active }"
                    @mousemove="handleCloseButton">
                    <div
                        class="relative w-7/8 max-w-video-modal h-0 bg-black-900 overflow-hidden transform transition-all duration-600 ease-out"
                        :class="{ 'translate-y-4': !active, 'translate-y-0 shadow-m': active }"
                        :style="{ height: containerHeight }">
                        <button
                            v-if="closeVisible"
                            class="close-button absolute top-5 right-5 w-10 h-10 text-primary-500 cursor-pointer origin-center transition-all duration-200 ease-out overflow-visible transform hover:scale-125 z-10"
                            autofocus
                            :aria-label="$globalLabels.close"
                            @click.stop.prevent="close">
                            <svg
                                class="w-full h-full fill-current"
                                viewBox="0 0 43 44"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M15.6256 27.1151L20.3674 22.3734L15.7504 17.7564L16.8734 16.6333L21.4904 21.2503L26.2322 16.5085L27.3053 17.5817L22.5635 22.3235L27.2055 26.9654L26.0824 28.0885L21.4405 23.4465L16.6987 28.1883L15.6256 27.1151Z" />
                            </svg>
                        </button>
                        <div v-if="isBrightcoveVideo()" ref="brightcoveEmbed" />
                        <div v-else ref="kalturaEmbed" :id="videoContainerId" class="w-full h-full" />
                    </div>
                </div>
            </dialog>
        </Transition>
    </Teleport>
</template>

<script lang="ts">
import brightcovePlayerLoader from '@brightcove/player-loader';
import { createKalturaPlayer, destroyKalturaPlayer } from '../../kaltura-player';
import type { KalturaPlayerInstance } from '../../kaltura-player';

export default {
    inject: ['$globalLabels', '$lang'],
    props: {
        videoId: { type: [String, Number], required: true },
        accountId: { type: String, required: true },
        partnerId: { type: String, default: '' },
        playerId: { type: String, required: true },
        videoType: { type: String, default: '' },
    },
    emits: ['fix-header', 'close'],
    data() {
        return {
            videoRef: null as { on?: (eventName: string, callback: () => void) => void; play?: () => void; pause?: () => void } | KalturaPlayerInstance | null,
            videoContainerId: '',
            containerHeight: '0',
            hidden: true,
            active: false,

            closeVisible: false,
            timer: '',
        };
    },
    watch: {
        async videoId() {
            if (this.videoId) {
                await this.loadVideo();
                this.openModal();
            } else if (this.active) {
                await this.close();
            }
        },
    },
    async mounted() {
        if (this.videoId) {
            this.openModal();
            await this.loadVideo();
        }
    },
    beforeUnmount() {
        this.close();
    },
    methods: {
        textLanguage(): string {
            return (this.$lang || document.documentElement.lang || '').toString();
        },
        isBrightcoveVideo(): boolean {
            return this.videoType !== 'kaltura';
        },
        handleCloseButton(): void {
            this.closeVisible = true;
            if (this.timer) clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.closeVisible = false;
            }, 3000);
        },
        async loadVideo() {
            if (this.videoRef && typeof this.videoRef.pause === 'function') {
                this.videoRef.pause();
            }
            destroyKalturaPlayer(this.videoRef as KalturaPlayerInstance | null);
            this.videoRef = null;
            this.videoContainerId = '';

            if (this.isBrightcoveVideo()) {
                await this.$nextTick();
                const brightcoveEmbedEl = this.$refs.brightcoveEmbed as HTMLElement;
                if (brightcoveEmbedEl) {
                    brightcoveEmbedEl.innerHTML = '';
                }
                const width = brightcoveEmbedEl?.clientWidth || 0;
                this.containerHeight = width > 0 ? `${Math.round(width * 9 / 16)}px` : '0';
                await this.$nextTick();
                const success = await brightcovePlayerLoader({
                    refNode: brightcoveEmbedEl,
                    accountId: this.accountId,
                    playerId: this.playerId,
                    videoId: this.videoId,
                    embedOptions: {
                        tagName: brightcovePlayerLoader.EMBED_TAG_NAME_VIDEO,
                        responsive: {
                            aspectRatio: '16:9',
                        },
                    },
                });

                this.videoRef = success.ref;

                window.requestAnimationFrame(() => {
                    const currentBrightcoveEmbedEl = this.$refs.brightcoveEmbed as HTMLElement;
                    this.containerHeight = `${currentBrightcoveEmbedEl?.clientHeight || 0}px`;
                });

                this.videoRef.on?.('loadedmetadata', () => {
                    if (this.videoRef && typeof this.videoRef.play === 'function') {
                        this.videoRef.play();
                    }
                });
                return;
            }

            this.videoContainerId = 'video-modal-' + Math.random().toString(16).slice(2);
            await this.$nextTick();
            const kalturaEmbedEl = this.$refs.kalturaEmbed as HTMLElement;
            const width = kalturaEmbedEl?.clientWidth || 0;
            this.containerHeight = width > 0 ? `${Math.round(width * 9 / 16)}px` : '0';
            await this.$nextTick();
            this.videoRef = await createKalturaPlayer({
                targetId: this.videoContainerId,
                partnerId: this.partnerId,
                playerId: this.playerId,
                entryId: this.videoId,
                textLanguage: this.textLanguage(),
                disableTranscriptPlugin: true,
            });
            window.requestAnimationFrame(() => {
                const currentKalturaEmbedEl = this.$refs.kalturaEmbed as HTMLElement;
                const currentWidth = currentKalturaEmbedEl?.clientWidth || 0;
                this.containerHeight = currentWidth > 0 ? `${Math.round(currentWidth * 9 / 16)}px` : this.containerHeight;
            });
        },
        close() {
            if (this.videoRef) {
                if (typeof this.videoRef.pause === 'function') {
                    this.videoRef.pause();
                }
                destroyKalturaPlayer(this.videoRef as KalturaPlayerInstance | null);
                this.videoRef = null;
            }

            this.closeModal();

            // Remove the old video and clear the height
            const brightcoveEmbedEl = this.$refs.brightcoveEmbed as HTMLElement;
            if (brightcoveEmbedEl) {
                brightcoveEmbedEl.innerHTML = '';
            }
            this.videoContainerId = '';
            this.containerHeight = '0';
            this.$emit('close');
        },
        openModal() {
            this.active = true;

            document.body.style.overflow = 'hidden';

            if (this.$refs.dialogEL && !this.$refs.dialogEL.open) {
                this.$refs.dialogEL.showModal();
            }
        },
        closeModal() {
            this.active = false;

            document.body.style.overflow = '';

            if (this.$refs.dialogEL.open) {
                this.$refs.dialogEL.close();
            }
        },
    },
};
</script>
